"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Upload, FileText } from "lucide-react";
export default function FlexibleLayout() {
  return <section id="flexiblelayout" className="relative bg-background py-16 px-4 md:py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div className="space-y-6">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground">
              Feed the Roots
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Upload your lump-sum contract here for PAM to begin extraction. Our Ground Truth engine will identify every submittal, schedule milestone, and safety hazard in seconds. Once uploaded, your deliverables will be sent to the PM Dashboard for your final approval.
            </p>
          </div>

          {}
          <FormProvider className="card bg-background border border-border p-8 md:p-12 shadow-medium" formId="contract-intake-form-flexible" sectionName="flexiblelayout" defaultValues={{
          name: "",
          email: "",
          companyName: "",
          contractFile: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          email: {
            required: true,
            pattern: "email"
          },
          companyName: {
            required: true,
            minLength: 2,
            maxLength: 150
          },
          contractFile: {
            required: true
          }
        }} submitText="Submit Contract" successMessage="Contract submitted successfully! We'll review it shortly.">
            <ContractIntakeForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ContractIntakeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8 px-6 bg-black text-[#00f3ff] border-[3px] border-[#00f3ff] rounded-none uppercase tracking-[2px] font-black animate-pulse" style={{
      boxShadow: '0 0 20px #00f3ff, inset 0 0 10px #00f3ff',
      fontFamily: "'Montserrat', sans-serif"
    }}>
        <div className="w-16 h-16 bg-[#00f3ff]/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#00f3ff]">
          <FileText className="w-8 h-8 text-[#00f3ff]" />
        </div>
        <h3 className="text-2xl font-black mb-2 uppercase tracking-[2px]">Success!</h3>
        <p className="mb-6 font-black uppercase tracking-[2px]">{successMessage}</p>
        <Button onClick={resetForm} className="bg-[#00f3ff] text-black hover:bg-[#00f3ff]/80 border-2 border-[#00f3ff] font-black uppercase tracking-[2px] transition-all duration-300">
          Submit Another Contract
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 100,
          message: "Name must not exceed 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className="border-border focus:border-primary focus:ring-primary/20 transition-all duration-300" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john.smith@company.com" {...field} className="border-border focus:border-primary focus:ring-primary/20 transition-all duration-300" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="companyName" rules={{
        required: "Company name is required",
        minLength: {
          value: 2,
          message: "Company name must be at least 2 characters"
        },
        maxLength: {
          value: 150,
          message: "Company name must not exceed 150 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Company Name</FormLabel>
              <FormControl>
                <Input placeholder="ABC Construction Inc." {...field} className="border-border focus:border-primary focus:ring-primary/20 transition-all duration-300" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="contractFile" rules={{
        required: "Contract file is required"
      }} render={({
        field: {
          onChange,
          value,
          ...field
        }
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Upload Contract (Roots)</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="file" accept=".pdf,.docx" onChange={e => {
              const file = e.target.files?.[0];
              onChange(file ? file.name : "");
            }} {...field} className="border-border focus:border-primary focus:ring-primary/20 transition-all duration-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-foreground hover:file:bg-primary/20 file:transition-all file:duration-300 cursor-pointer" />
                  <Upload className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                </div>
              </FormControl>
              <p className="text-sm text-muted-foreground mt-1">
                Accepted formats: PDF, DOCX (Max 10MB)
              </p>
              <FormMessage />
            </FormItem>} />

        {}
        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm font-medium">{submitError}</p>
          </div>}

        {}
        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary-hover shadow-soft hover:shadow-medium transition-all duration-300 ease-[var(--ease-spring)] h-12 text-base font-semibold">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Submitting Contract...
            </> : <>
              <Upload className="mr-2 size-5" />
              Submit
            </>}
        </Button>
      </div>
    </Form>;
}